import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import Button from "@material-ui/core/Button";
import { Save as SaveIcon, PictureAsPdf as PdfIcon, Visibility as VisibilityIcon, Add as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";
import Switch from "@material-ui/core/Switch";
import LoadingOverlay from "../LoadingOverlay";
import Header from "../Header/Header";
import NumDossCISSCT from "../NumDossCISSCT";
import DocMenu from "../DocMenu";
import InformationDialog from '../InformationDialog';
import axios from "axios";
import { changeTitleName, changePdfFileName, changeIndication } from "../../redux/actions/emailAction";
import { getConvocationInfos } from './Utils';
import { v4 as uuidv4 } from "uuid";
import "../../Styles/CISSCT/Report.scss";
import GenPdf from "../GenPdf";

const Report = () => {
  const dispatch = useDispatch();
  const dossierValue = useSelector((state) => state.devis.dossierValue);
  const index = useSelector((state) => state.cissct.index);

  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isCustomType, setIsCustomType] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [customTypeValue, setCustomTypeValue] = useState("");

  const initialValue = {
    date: new Date().toISOString().slice(0, 10),
    meetingType: "Installation CISSCT",
    meetingDate: "",
    meetingTime: "",
    meetingLocation: "",
    operation: "",
    address: "",
    projectOwner: "",
    projectManager: "",
    spsCoordinator: "COORDOGEC",
    worksDirector: "",
    cissctPresident: "Jean-Marie SAINT-GERMAIN",

    // Présences
    presentMembers: [
      { id: uuidv4(), name: "", company: "", contact: "", role: "" }
    ],
    absentMembers: [
      { id: uuidv4(), name: "", contact: "", reason: "" }
    ],

    // Ordre du jour & discussions
    agendaItems: [
      { id: uuidv4(), title: "", content: "" }
    ],

    // Décisions & Actions
    decisions: [
      { id: uuidv4(), description: "" }
    ],
    actions: [
      { id: uuidv4(), action: "", responsible: "", deadline: "" }
    ],

    // Observations & informations
    safetyObservations: "",
    incidents: "",
    nextMeetingDate: "",
    nextMeetingTime: "",
    closingTime: "",
    additionalNotes: "",

    // Signature
    signature: false
  }

  const [formData, setFormData] = useState(initialValue);
  const [errors, setErrors] = useState({});

  const fetchData = useCallback(async () => {
    if (!dossierValue || !index) return;
    setIsLoading(true);

    try {
      const result = await getConvocationInfos(dossierValue, index);
      const response = Object.keys(result).length > 0;

      if (!response) {
        setIsCustomType(false);
        setCustomTypeValue('Installation CISSCT');
        setFormData(initialValue);
      } else {
        const isCustom = result.meetingType !== 'Installation CISSCT' && result.meetingType !== 'Réunion périodique' && result.meetingType !== 'Réunion extraordinaire';
        setIsCustomType(isCustom);

        if (isCustom) {
          setCustomTypeValue(result.meetingType);
        } else {
          setCustomTypeValue('');
        }
        setFormData(prev => ({
          ...prev,
          date: new Date().toISOString().slice(0, 10),
          meetingType: result.meetingType,
          meetingDate: result.meetingDate,
          meetingTime: result.meetingTime,
          meetingLocation: result.meetingLocation,
          operation: result.operation,
          address: result.address,
          projectOwner: result.projectOwner,
          projectManager: result.projectManager,
          spsCoordinator: result.spsCoordinator,
          worksDirector: result.worksDirector,
          cissctPresident: result.cissctPresident,
          presentMembers: [
            { id: uuidv4(), name: "", company: "", contact: "", role: "" }
          ],
          absentMembers: [
            { id: uuidv4(), name: "", contact: "", reason: "" }
          ],
          agendaItems: [
            { id: uuidv4(), title: "", content: "" }
          ],
          decisions: [
            { id: uuidv4(), description: "" }
          ],
          actions: [
            { id: uuidv4(), action: "", responsible: "", deadline: "" }
          ],
          safetyObservations: "",
          incidents: "",
          nextMeetingDate: "",
          nextMeetingTime: "",
          closingTime: "",
          additionalNotes: "",
          signature: false
        }));
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des informations:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dossierValue, index]);

  const handleGetReport = useCallback(async () => {
    setErrors({});
    if (!dossierValue || !index) return;
    setIsLoading(true);

    try {
      const response = await axios.get(`/api/cissct/getReport/${dossierValue}/${index}`);
      const parsed = JSON.parse(response.data.data);
      const standardTypes = ['Installation CISSCT', 'Réunion périodique', 'Réunion extraordinaire'];

      setFormData(prev => ({
        ...prev,
        ...parsed
      }));

      if (parsed.meetingType && !standardTypes.includes(parsed.meetingType)) {
        setIsCustomType(true);
        setCustomTypeValue(parsed.meetingType);
      } else {
        setIsCustomType(false);
        setCustomTypeValue("");
      }

    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          fetchData();
          return;
        }
        console.error("Erreur API:", error.response.data?.error || error.message);
      } else {
        console.error("Erreur réseau:", error.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [dossierValue, fetchData]);

  useEffect(() => {
    handleGetReport()
  }, [dossierValue, index, fetchData]);

  useEffect(() => {
    dispatch(changeIndication("Procès-verbal CISSCT"));
    handleChangeTitleName();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'meetingType') {
      if (value === 'Autre') {
        setIsCustomType(true);
        setFormData(prev => ({ ...prev, meetingType: '' }));
        return;
      }
      setIsCustomType(false);
      setCustomTypeValue('');
      setFormData(prev => ({ ...prev, meetingType: value }));
      if (errors.meetingType) {
        setErrors(prev => ({ ...prev, meetingType: null }));
      }
      return;
    }

    handleInputChange(name, value);
  };

  const handleCustomTypeChange = (e) => {
    const value = e.target.value;
    setCustomTypeValue(value);
    setFormData(prev => ({ ...prev, meetingType: value }));
  };

  const handleBackToSelect = () => {
    setIsCustomType(false);
    setCustomTypeValue('');
    setFormData(prev => ({ ...prev, meetingType: 'Installation CISSCT' }));
  };

  // Gestion des membres présents
  const handleAddPresentMember = () => {
    setFormData(prev => ({
      ...prev,
      presentMembers: [...prev.presentMembers, { id: uuidv4(), name: "", company: "", contact: "", role: "" }]
    }));
  };

  const handleRemovePresentMember = (index) => {
    if (formData.presentMembers.length === 1) {
      setFormData(prev => ({
        ...prev,
        presentMembers: [{ id: uuidv4(), name: "", company: "", contact: "", role: "" }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        presentMembers: prev.presentMembers.filter((_, i) => i !== index)
      }));
    }
  };

  const handlePresentMemberChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      presentMembers: prev.presentMembers.map((member, i) =>
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  // Gestion des membres absents
  const handleAddAbsentMember = () => {
    setFormData(prev => ({
      ...prev,
      absentMembers: [...prev.absentMembers, { id: uuidv4(), name: "", contact: "", reason: "" }]
    }));
  };

  const handleRemoveAbsentMember = (index) => {
    if (formData.absentMembers.length === 1) {
      setFormData(prev => ({
        ...prev,
        absentMembers: [{ id: uuidv4(), name: "", contact: "", reason: "" }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        absentMembers: prev.absentMembers.filter((_, i) => i !== index)
      }));
    }
  };

  const handleAbsentMemberChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      absentMembers: prev.absentMembers.map((member, i) =>
        i === index ? { ...member, [field]: value } : member
      )
    }));
  };

  // Gestion des points de l'ordre du jour
  const handleAddAgendaItem = () => {
    setFormData(prev => ({
      ...prev,
      agendaItems: [...prev.agendaItems, { id: uuidv4(), title: "", content: "" }]
    }));
  };

  const handleRemoveAgendaItem = (index) => {
    if (formData.agendaItems.length === 1) {
      setFormData(prev => ({
        ...prev,
        agendaItems: [{ id: uuidv4(), title: "", content: "" }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        agendaItems: prev.agendaItems.filter((_, i) => i !== index)
      }));
    }
  };

  const handleAgendaItemChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      agendaItems: prev.agendaItems.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Gestion des décisions
  const handleAddDecision = () => {
    setFormData(prev => ({
      ...prev,
      decisions: [...prev.decisions, { id: uuidv4(), description: "" }]
    }));
  };

  const handleRemoveDecision = (index) => {
    if (formData.decisions.length === 1) {
      setFormData(prev => ({
        ...prev,
        decisions: [{ id: uuidv4(), description: "" }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        decisions: prev.decisions.filter((_, i) => i !== index)
      }));
    }
  };

  const handleDecisionChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      decisions: prev.decisions.map((decision, i) =>
        i === index ? { ...decision, description: value } : decision
      )
    }));
  };

  // Gestion des actions
  const handleAddAction = () => {
    setFormData(prev => ({
      ...prev,
      actions: [...prev.actions, { id: uuidv4(), action: "", responsible: "", deadline: "" }]
    }));
  };

  const handleRemoveAction = (index) => {
    if (formData.actions.length === 1) {
      setFormData(prev => ({
        ...prev,
        actions: [{ id: uuidv4(), action: "", responsible: "", deadline: "" }]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        actions: prev.actions.filter((_, i) => i !== index)
      }));
    }
  };

  const handleActionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.map((action, i) =>
        i === index ? { ...action, [field]: value } : action
      )
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.date) newErrors.date = "Date obligatoire";
    if (!formData.meetingDate) newErrors.meetingDate = "Date obligatoire";
    if (!formData.meetingTime) newErrors.meetingTime = "Heure obligatoire";
    if (!formData.closingTime) newErrors.closingTime = "Heure obligatoire";
    if (!formData.meetingLocation) newErrors.meetingLocation = "Lieu obligatoire";
    if (!formData.operation) newErrors.operation = "Opération obligatoire";
    if (!formData.address) newErrors.address = "Adresse obligatoire";
    if (!formData.projectOwner) newErrors.projectOwner = "Maître d'ouvrage obligatoire";
    if (!formData.spsCoordinator) newErrors.spsCoordinator = "Coordonnateur SPS obligatoire";
    if (!formData.cissctPresident) newErrors.cissctPresident = "Président CISSCT obligatoire";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = async () => {
    const isValid = validateForm();
    if (!isValid) {
      setOpenDialog(true);
      return;
    };

    const isEmptyObject = (obj, fieldsToCheck) => {
      return fieldsToCheck.every(field => !obj[field] || obj[field].trim() === '');
    };

    const cleanedFormData = {
      ...formData,
      presentMembers: formData.presentMembers.filter(member =>
        !isEmptyObject(member, ['name', 'company', 'contact', 'role'])
      ),
      absentMembers: formData.absentMembers.filter(member =>
        !isEmptyObject(member, ['name', 'contact', 'reason'])
      ),
      agendaItems: formData.agendaItems.filter(item =>
        !isEmptyObject(item, ['title', 'content'])
      ),
      decisions: formData.decisions.filter(decision =>
        !isEmptyObject(decision, ['description'])
      ),
      actions: formData.actions.filter(action =>
        !isEmptyObject(action, ['action', 'responsible', 'deadline'])
      )
    };

    setIsLoading(true);
    try {
      const requestOptions = {
        ndossier: dossierValue,
        index: index,
        data: JSON.stringify(cleanedFormData)
      };

      const response = await axios.post('/api/cissct/saveReport', requestOptions, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status !== 200) {
        throw new Error("Erreur lors de l'enregistrement du PV CISSCT");
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du PV CISSCT:", error);
    } finally {
      handleGetReport();
      setIsLoading(false);
    }
  };


  const handleGenClick = useCallback(async () => {
    const isValid = validateForm();
    if (!isValid) return;

    setIsLoading(true);
    setFile(null);

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ndossier: dossierValue,
        index: index,
        data: formData
      }),
    };

    try {
      const response = await fetch("/api/cissct/genReportPdf", requestOptions);
      const blob = await response.blob();
      if (blob) {
        setFile(blob);
        setIsOpen(true);
      }
    } catch (err) {
      console.log("Erreur lors de la génération du PDF :", err);
    } finally {
      setIsLoading(false);
    }
  }, [dossierValue, index, formData]);

  const handleShowPdf = useCallback(async () => {
    setIsLoading(true);
    setFile(null);
    try {
      const response = await fetch(`/api/cissct/getReportPdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ndossier: dossierValue,
          index: index,
          type: 'pdf'
        }),
      });
      const blob = await response.blob();
      if (blob.size > 100) {
        const fileName = response.headers.get('Content-Disposition')?.split('filename=')[1] || 'pv.pdf';
        dispatch(changePdfFileName(fileName));
        setFile(blob);
        setIsOpen(true);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }, [dossierValue, index]);

  const handleGetWordFile = useCallback(async () => {
    setIsLoading(true);
    setFile(null);
    try {
      const response = await fetch(`/api/cissct/getReportPdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ndossier: dossierValue,
          index: index,
          type: 'word',
        }),
      });
      const blob = await response.blob();
      if (blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        const fileName = response.headers.get('Content-Disposition')?.split('filename=')[1] || 'CISSCT_PROCES_VERBAL.docx';
        dispatch(changePdfFileName(fileName));
        setFile(blob);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }, [dossierValue, index]);

  const handleGetPdfFile = useCallback(async () => {
    setIsLoading(true);
    setFile(null);
    try {
      const response = await fetch(`/api/cissct/getReportPdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ndossier: dossierValue,
          index: index,
          type: 'pdf',
        }),
      });
      const blob = await response.blob();
      if (blob.size > 100) {
        const fileName = response.headers.get('Content-Disposition')?.split('filename=')[1] || `CISSCT_PROCES_VERBAL.pdf`;
        dispatch(changePdfFileName(fileName));
        setFile(blob);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }, [dossierValue, index]);

  const handleChangeTitleName = () => {
    dispatch(changeTitleName(`Procès-verbal`));
  };

  const disableRemovePresentMemberButton = formData.presentMembers.length === 1 && !formData.presentMembers[0].name && !formData.presentMembers[0].company && !formData.presentMembers[0].contact && !formData.presentMembers[0].role;

  const disableRemoveAbsentMemberButton = formData.absentMembers.length === 1 && !formData.absentMembers[0].name && !formData.absentMembers[0].contact && !formData.absentMembers[0].reason;

  const disableRemoveAgendaItemButton = formData.agendaItems.length === 1 && !formData.agendaItems[0].title && !formData.agendaItems[0].content;

  const disableRemoveDecisionButton = formData.decisions.length === 1 && !formData.decisions[0].description;

  const disableRemoveActionButton = formData.actions.length === 1 && !formData.actions[0].action && !formData.actions[0].responsible && !formData.actions[0].deadline;

  return (
    <div className="cissct-pv">
      {isLoading && <LoadingOverlay />}
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
        updateIndication={() => dispatch(changeIndication('Procès-verbal'))}
      />
      <h1 className="title">Procès-verbal CISSCT</h1>
      <NumDossCISSCT />

      <div className="container">
        <div className="form-content">
          {/* Section En-tête */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="date">Date du Procès-verbal *</label>
                <input
                  id="date"
                  type="date"
                  name="date"
                  style={{ width: '150px' }}
                  value={formData.date}
                  onChange={handleChange}
                  className={errors.date ? 'error' : ''}
                />
                {errors.date && <span className="error-message">{errors.date}</span>}
              </div>
            </div>

            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
              </svg>
              Informations de la réunion
            </h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="meetingType">Type de réunion *</label>
                {isCustomType ? (
                  <div className="custom-type-wrapper">
                    <input
                      type="text"
                      value={customTypeValue}
                      onChange={handleCustomTypeChange}
                      placeholder="Précisez le type de réunion..."
                      autoFocus
                    />
                    <button
                      type="button"
                      className="btn-cancel-custom"
                      onClick={handleBackToSelect}
                      title="Revenir à la liste"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <select
                    id="meetingType"
                    name="meetingType"
                    value={formData.meetingType}
                    onChange={handleChange}
                  >
                    <option>Installation CISSCT</option>
                    <option>Réunion périodique</option>
                    <option>Réunion extraordinaire</option>
                    <option>Autre</option>
                  </select>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="meetingDate">Date de la réunion *</label>
                <input
                  type="date"
                  id="meetingDate"
                  name="meetingDate"
                  value={formData.meetingDate}
                  onChange={handleChange}
                  className={errors.meetingDate ? 'error' : ''}
                />
                {errors.meetingDate && <span className="error-message">{errors.meetingDate}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="meetingTime">Heure de début *</label>
                <input
                  type="time"
                  id="meetingTime"
                  name="meetingTime"
                  value={formData.meetingTime}
                  onChange={handleChange}
                  className={errors.meetingTime ? 'error' : ''}
                />
                {errors.meetingTime && <span className="error-message">{errors.meetingTime}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="closingTime">Heure de fin *</label>
                <input
                  type="time"
                  id="closingTime"
                  name="closingTime"
                  value={formData.closingTime}
                  onChange={handleChange}
                  className={errors.closingTime ? 'error' : ''}
                />
                {errors.closingTime && <span className="error-message">{errors.closingTime}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="meetingLocation">Lieu de la réunion *</label>
                <input
                  type="text"
                  id="meetingLocation"
                  name="meetingLocation"
                  value={formData.meetingLocation}
                  onChange={handleChange}
                  placeholder="Salle de réunion, base-vie, siège..."
                  className={errors.meetingLocation ? 'error' : ''}
                />
                {errors.meetingLocation && <span className="error-message">{errors.meetingLocation}</span>}
              </div>
            </div>
          </div>

          {/* Section Chantier & Acteurs */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5 8 5.961 14.154 3.5 8.186 1.113zM15 4.239l-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923l6.5 2.6zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464L7.443.184z" />
              </svg>
              Chantier & acteurs du projet
            </h2>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="operation">Opération *</label>
                <input
                  type="text"
                  id="operation"
                  name="operation"
                  value={formData.operation}
                  onChange={handleChange}
                  placeholder="Ex : Construction d'un immeuble R+8"
                  className={errors.operation ? 'error' : ''}
                />
                {errors.operation && <span className="error-message">{errors.operation}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="address">Adresse du chantier *</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Ex : 123 Rue de la République, 75001 - Paris"
                  className={errors.address ? 'error' : ''}
                />
                {errors.address && <span className="error-message">{errors.address}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="projectOwner">Maître d'ouvrage *</label>
                <input
                  type="text"
                  id="projectOwner"
                  name="projectOwner"
                  value={formData.projectOwner}
                  onChange={handleChange}
                  placeholder="Ex: SCCV"
                  className={errors.projectOwner ? 'error' : ''}
                />
                {errors.projectOwner && <span className="error-message">{errors.projectOwner}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="projectManager">Maître d'œuvre</label>
                <input
                  type="text"
                  id="projectManager"
                  name="projectManager"
                  value={formData.projectManager}
                  onChange={handleChange}
                  placeholder="Cabinet d'architecture, Bureau d'études..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="spsCoordinator">Coordonnateur SPS *</label>
                <input
                  type="text"
                  id="spsCoordinator"
                  name="spsCoordinator"
                  value={formData.spsCoordinator}
                  onChange={handleChange}
                  placeholder="Ex: COORDOGEC"
                  className={errors.spsCoordinator ? 'error' : ''}
                />
                {errors.spsCoordinator && <span className="error-message">{errors.spsCoordinator}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="worksDirector">Directeur de travaux</label>
                <input
                  type="text"
                  id="worksDirector"
                  name="worksDirector"
                  value={formData.worksDirector}
                  onChange={handleChange}
                  placeholder="Responsable technique maîtrise d'œuvre"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="cissctPresident">Président du CISSCT *</label>
                <input
                  type="text"
                  id="cissctPresident"
                  name="cissctPresident"
                  value={formData.cissctPresident}
                  onChange={handleChange}
                  placeholder="Nom et fonction du président"
                  className={errors.cissctPresident ? 'error' : ''}
                />
                {errors.cissctPresident && <span className="error-message">{errors.cissctPresident}</span>}
              </div>
            </div>
          </div>

          {/* Section Participants présents */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1h8zm-7.978-1A.261.261 0 0 1 7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002a.274.274 0 0 1-.014.002H7.022zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM6.936 9.28a5.88 5.88 0 0 0-1.23-.247A7.35 7.35 0 0 0 5 9c-4 0-5 3-5 4 0 .667.333 1 1 1h4.216A2.238 2.238 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816zM4.92 10A5.493 5.493 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275zM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0zm3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
              </svg>
              Participants présents
            </h2>

            <p className="help-text">
              Liste des membres présents à la réunion avec leurs fonctions et entreprises.
            </p>

            {formData.presentMembers.map((member, index) => (
              <div key={member.id} className="dynamic-row">
                <div className="dynamic-row-content present-members">
                  <div className="form-group">
                    <label>Nom & Prénom</label>
                    <input
                      type="text"
                      value={member.name}
                      onChange={(e) => handlePresentMemberChange(index, 'name', e.target.value)}
                      placeholder="Ex: Jean Dupont"
                    />
                  </div>
                  <div className="form-group">
                    <label>Entreprise</label>
                    <input
                      type="text"
                      value={member.company}
                      onChange={(e) => handlePresentMemberChange(index, 'company', e.target.value)}
                      placeholder="Ex: SARL Construction"
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact</label>
                    <input
                      type="text"
                      value={member.contact}
                      onChange={(e) => handlePresentMemberChange(index, 'contact', e.target.value)}
                      placeholder="Ex: 06 12 34 56 78"
                    />
                  </div>
                  <div className="form-group">
                    <label>Fonction</label>
                    <input
                      type="text"
                      value={member.role}
                      onChange={(e) => handlePresentMemberChange(index, 'role', e.target.value)}
                      placeholder="Ex: Chef de chantier"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => handleRemovePresentMember(index)}
                  title="Supprimer"
                  disabled={disableRemovePresentMemberButton}
                >
                  <DeleteIcon />
                </button>
              </div>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddPresentMember}
              className="btn-add"
            >
              Ajouter un participant
            </Button>
          </div>

          {/* Section Participants absents */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M15.854 5.146a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L14.293 6 12.146 3.854a.5.5 0 1 1 .708-.708l3 3z" />
                <path fillRule="evenodd" d="M1 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
              </svg>
              Participants absents
            </h2>

            <p className="help-text">
              Liste des membres absents à la réunion avec le motif d'absence si connu.
            </p>

            {formData.absentMembers.map((member, index) => (
              <div key={member.id} className="dynamic-row">
                <div className="dynamic-row-content absent-members">
                  <div className="form-group">
                    <label>Nom & Prénom</label>
                    <input
                      type="text"
                      value={member.name}
                      onChange={(e) => handleAbsentMemberChange(index, 'name', e.target.value)}
                      placeholder="Ex: Marie Martin"
                    />
                  </div>
                  <div className="form-group">
                    <label>Contact</label>
                    <input
                      type="text"
                      value={member.contact}
                      onChange={(e) => handleAbsentMemberChange(index, 'contact', e.target.value)}
                      placeholder="Ex: 06 12 34 56 78"
                    />
                  </div>
                  <div className="form-group">
                    <label>Motif d'absence</label>
                    <input
                      type="text"
                      value={member.reason}
                      onChange={(e) => handleAbsentMemberChange(index, 'reason', e.target.value)}
                      placeholder="Ex: Congé maladie, Excusé..."
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => handleRemoveAbsentMember(index)}
                  title="Supprimer"
                  disabled={disableRemoveAbsentMemberButton}
                >
                  <DeleteIcon />
                </button>
              </div>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddAbsentMember}
              className="btn-add"
            >
              Ajouter un absent
            </Button>
          </div>

          {/* Section Ordre du jour & Discussions */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z" />
              </svg>
              Ordre du jour & discussions
            </h2>

            <p className="help-text">
              Points abordés lors de la réunion avec le résumé des discussions pour chaque point.
            </p>

            {formData.agendaItems.map((item, index) => (
              <div key={item.id} className="dynamic-row vertical">
                <div className='dynamic-row-container'>
                  <div className="dynamic-row-header">
                    <span className="item-number">Point {index + 1}</span>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => handleRemoveAgendaItem(index)}
                      title="Supprimer"
                      disabled={disableRemoveAgendaItemButton}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                  <div className="form-group full-width">
                    <label>Titre du point</label>
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => handleAgendaItemChange(index, 'title', e.target.value)}
                      placeholder="Ex: Présentation du plan de prévention"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Discussion / Contenu</label>
                    <textarea
                      value={item.content}
                      onChange={(e) => handleAgendaItemChange(index, 'content', e.target.value)}
                      rows={4}
                      placeholder="Résumé des discussions, constats, remarques..."
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddAgendaItem}
              className="btn-add"
            >
              Ajouter un point
            </Button>
          </div>

          {/* Section Décisions prises */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
              </svg>
              Décisions prises
            </h2>

            <p className="help-text">
              Décisions officielles prises au cours de la réunion.
            </p>

            {formData.decisions.map((decision, index) => (
              <div key={decision.id} className="dynamic-row vertical">
                <div className='dynamic-row-container'>
                  <div className="dynamic-row-header">
                    <span className="item-number">Décision {index + 1}</span>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => handleRemoveDecision(index)}
                      title="Supprimer"
                      disabled={disableRemoveDecisionButton}
                    >
                      <DeleteIcon />
                    </button>
                  </div>
                  <div className="form-group full-width">
                    <textarea
                      value={decision.description}
                      onChange={(e) => handleDecisionChange(index, e.target.value)}
                      rows={2}
                      placeholder="Description de la décision prise..."
                    />
                  </div>
                </div>
              </div>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddDecision}
              className="btn-add"
            >
              Ajouter une décision
            </Button>
          </div>

          {/* Section Actions à mener */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path fillRule="evenodd" d="M6 3.5A1.5 1.5 0 0 1 7.5 2h1A1.5 1.5 0 0 1 10 3.5v1A1.5 1.5 0 0 1 8.5 6v1H14a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-1 0V8h-5v.5a.5.5 0 0 1-1 0V8h-5v.5a.5.5 0 0 1-1 0v-1A.5.5 0 0 1 2 7h5.5V6A1.5 1.5 0 0 1 6 4.5v-1zM8.5 5a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1zM0 11.5A1.5 1.5 0 0 1 1.5 10h1A1.5 1.5 0 0 1 4 11.5v1A1.5 1.5 0 0 1 2.5 14h-1A1.5 1.5 0 0 1 0 12.5v-1zm1.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1zm4.5.5A1.5 1.5 0 0 1 7.5 10h1a1.5 1.5 0 0 1 1.5 1.5v1A1.5 1.5 0 0 1 8.5 14h-1A1.5 1.5 0 0 1 6 12.5v-1zm1.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1zm4.5.5a1.5 1.5 0 0 1 1.5-1.5h1a1.5 1.5 0 0 1 1.5 1.5v1a1.5 1.5 0 0 1-1.5 1.5h-1a1.5 1.5 0 0 1-1.5-1.5v-1zm1.5-.5a.5.5 0 0 0-.5.5v1a.5.5 0 0 0 .5.5h1a.5.5 0 0 0 .5-.5v-1a.5.5 0 0 0-.5-.5h-1z" />
              </svg>
              Actions à mener
            </h2>

            <p className="help-text">
              Liste des actions à réaliser suite à la réunion avec responsables et échéances.
            </p>

            {formData.actions.map((action, index) => (
              <div key={action.id} className="dynamic-row">
                <div className="dynamic-row-content">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label>Action à réaliser</label>
                    <input
                      type="text"
                      value={action.action}
                      onChange={(e) => handleActionChange(index, 'action', e.target.value)}
                      placeholder="Ex: Mise en place des garde-corps zone A"
                    />
                  </div>
                  <div className="form-group">
                    <label>Responsable</label>
                    <input
                      type="text"
                      value={action.responsible}
                      onChange={(e) => handleActionChange(index, 'responsible', e.target.value)}
                      placeholder="Nom / Entreprise"
                    />
                  </div>
                  <div className="form-group">
                    <label>Échéance</label>
                    <input
                      type="date"
                      value={action.deadline}
                      onChange={(e) => handleActionChange(index, 'deadline', e.target.value)}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-remove"
                  onClick={() => handleRemoveAction(index)}
                  title="Supprimer"
                  disabled={disableRemoveActionButton}
                >
                  <DeleteIcon />
                </button>
              </div>
            ))}

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddAction}
              className="btn-add"
            >
              Ajouter une action
            </Button>
          </div>

          {/* Section Observations & Informations complémentaires */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
              </svg>
              Observations & informations complémentaires
            </h2>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="safetyObservations">Observations en matière de sécurité</label>
                <textarea
                  id="safetyObservations"
                  name="safetyObservations"
                  value={formData.safetyObservations}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Constats sur les conditions de travail, équipements de protection, état des installations..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="incidents">Incidents / Accidents survenus</label>
                <textarea
                  id="incidents"
                  name="incidents"
                  value={formData.incidents}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Description des incidents ou accidents survenus depuis la dernière réunion..."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nextMeetingDate">Date de la prochaine réunion</label>
                <input
                  type="date"
                  id="nextMeetingDate"
                  name="nextMeetingDate"
                  value={formData.nextMeetingDate}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="nextMeetingTime">Heure</label>
                <input
                  type="time"
                  id="nextMeetingTime"
                  name="nextMeetingTime"
                  value={formData.nextMeetingTime}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="additionalNotes">Notes complémentaires</label>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Autres informations, remarques générales..."
                />
              </div>
            </div>
          </div>

          {/* Section Signature */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M13.5 1a1.5 1.5 0 0 1 1.5 1.5v10A1.5 1.5 0 0 1 13.5 14h-11A1.5 1.5 0 0 1 1 12.5v-10A1.5 1.5 0 0 1 2.5 1h11zm-11-1A2.5 2.5 0 0 0 0 2.5v10A2.5 2.5 0 0 0 2.5 15h11a2.5 2.5 0 0 0 2.5-2.5v-10A2.5 2.5 0 0 0 13.5 0h-11z" />
                <path d="M4 9.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zM4 7a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3A.5.5 0 0 1 4 7z" />
              </svg>
              Signature
            </h2>

            <div className="form-row">
              <div className="form-group full-width">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px',
                  background: formData.signature ? '#667eea14' : '#f7fafc',
                  borderRadius: '12px',
                  border: `2px solid ${formData.signature ? '#667eea' : '#e2e8f0'}`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                  onClick={() => handleInputChange('signature', !formData.signature)}
                >
                  <Switch
                    checked={formData.signature}
                    onChange={(e) => handleInputChange('signature', e.target.checked)}
                    color="primary"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontWeight: 600,
                      color: formData.signature ? '#667eea' : '#4a5568',
                      fontSize: '15px'
                    }}>
                      Activer la signature du procès-verbal
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: '#718096',
                      marginTop: '4px'
                    }}>
                      {formData.signature
                        ? 'La signature sera incluse dans le document PDF'
                        : 'Cliquez pour activer la signature'}
                    </div>
                  </div>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: formData.signature ? '#667eea' : '#e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease'
                  }}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 16 16"
                      fill={formData.signature ? 'white' : '#718096'}
                    >
                      <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="group-btn">
          <Button
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveClick}
            disabled={!index}
          >
            Enregistrer
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={handleGenClick}
            disabled={!index}
          >
            Générer
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<VisibilityIcon />}
            onClick={handleShowPdf}
            disabled={!index}
          >
            Afficher
          </Button>
          <Button
            variant="contained"
            className="btn-reset"
            onClick={fetchData}
            disabled={!index}
          >
            Annuler
          </Button>
        </div>
      </div>
      <GenPdf
        file={file}
        isOpen={isOpen}
        closeDoc={() => setIsOpen(false)}
        scale={1}
      />
      <InformationDialog
        isOpen={openDialog}
        title={'INFORMATION'}
        information={'Champs obligatoires manquants.'}
        handleClose={() => setOpenDialog(false)}
      />
    </div>
  );
};

export default Report;
